// @flow

import invariant from 'invariant';
import camelCase from 'lodash/camelCase';
import snakeCase from 'lodash/snakeCase';
import type { Saga } from 'redux-saga';
import { createSelector, type OutputSelector } from 'reselect';
import { put, select, take } from 'typed-redux-saga/macro';

import type { GlobalStateType } from 'Lib/reduxType';

import { getIsLifecycleTriggered } from './app/selectors';
import { LIFECYCLE_ACTION_TYPE_PREFIX } from './constants';

const LIFECYCLE_MAPPING = new Map();

export type Lifecycle = {
  name: string,
  key: string,
  type: string,
  isTriggered: OutputSelector<GlobalStateType, *, boolean>,
  block: () => Saga<number>,
  reset: () => Saga<void>,
  trigger: () => Saga<void>,
};

/**
 * Registers a lifecycle
 */
export function createLifecycle(name: string): Lifecycle {
  invariant(!LIFECYCLE_MAPPING.has(name), `Cannot create duplicate lifecycle "${name}"`);

  const key = camelCase(name);
  const type = `${LIFECYCLE_ACTION_TYPE_PREFIX}${snakeCase(name).toUpperCase()}`;
  const isTriggered = createSelector(
    (state) => state,
    () => key,
    getIsLifecycleTriggered
  );

  const lifecycle = {
    name,
    key,
    type,
    isTriggered,
    *block(): Saga<number> {
      if (yield* select(isTriggered)) {
        return 0;
      }
      const start = Date.now();
      const { timestamp } = yield* take(type);
      return timestamp - start;
    },
    *reset(): Saga<void> {
      yield* put({ type, timestamp: Date.now(), payload: { [key]: false } });
    },
    *trigger(): Saga<void> {
      if (!(yield* select(isTriggered))) {
        yield* put({ type, timestamp: Date.now(), payload: { [key]: true } });
      }
    },
  };

  LIFECYCLE_MAPPING.set(name, lifecycle);

  return lifecycle;
}

/**
 * App Lifecycles
 */
export const appInitializeStart = createLifecycle('APP_INITIALIZE_START');
export const appInitializeComplete = createLifecycle('APP_INITIALIZE_COMPLETE');
export const initialAuthCheckComplete = createLifecycle('INITIAL_AUTH_CHECK_COMPLETE');

export const privacyPreferencesSet = createLifecycle('PRIVACY_PREFERENCES_SET');
